import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  alpha,
} from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { useAuth } from '../../AuthContext';
import { tokens } from '../../theme';
import { useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import LaptopIcon from '@mui/icons-material/Laptop';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

import { buildUi } from '../global/uiTokens';
import {
  DetailRow,
  StatusChip,
  EmptyState,
  Loading,
  RecordCard,
  SectionCard,
  dialogPaperSx,
  fieldSx,
  outlineBtn,
  primaryBtn,
} from './userShared';

const Borrow = ({ userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';
  const ui = useMemo(() => buildUi(colors, isDark), [colors, isDark]);
  const { getData, addData, updateData, deleteData } = useAuth();
  const [borrows, setBorrows] = useState([]);
  const [items, setItems] = useState([]);
  const [openBorrowDialog, setOpenBorrowDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [selectedBorrowId, setSelectedBorrowId] = useState(null);
  const [formData, setFormData] = useState({
    equipment: '',
    duration: '',
    type: '',
    destination: '',
    description: '',
    date: ''
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    async function fetchData() {
      try {
        const borrowResponse = await getData('proccess.php', 'borrow_V2', userId);
        if (borrowResponse.success) {
          setBorrows(borrowResponse.data);
        }

        const inventoryResponse = await getData('proccess.php', 'inventory');
        if (inventoryResponse.success) {
          setItems(inventoryResponse.data?.filter((item) => item.categoryType === 'Laptop' && item.categoryType === 'Charger' && item.product_condition !== 'Damaged' && item.product_condition !== 'Refurbished') || []);
        }

      } catch (err) {
        toast.error('Network error: Failed to fetch data');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [userId, getData]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBorrowSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        const response = await updateData('proccess.php', 'borrow_V2', {
          staff_id: userId,
          date: formData.date,
          equipment: formData.equipment,
          duration: formData.duration,
          type: formData.type,
          destination: formData.destination,
          description: formData.description,
          status: '0'
        }, selectedBorrowId);
        if (response.success) {
          toast.success('Borrow request updated successfully');
          setBorrows(borrows.map(borrow => 
            borrow.id === selectedBorrowId ? { ...borrow, ...formData } : borrow
          ));
          setOpenBorrowDialog(false);
          setFormData({ equipment: '', duration: '', type: '', destination: '', description: '', date: '' });
          setIsEditMode(false);
          setSelectedBorrowId(null);
        } else {
          toast.error(response.message || 'Failed to update borrow request');
        }
      } else {
        const response = await addData('proccess.php', 'borrow_V2', {
          staff_id: userId,
          equipment: formData.equipment,
          duration: formData.duration,
          type: formData.type,
          destination: formData.destination,
          description: formData.description
        });
        
        if (response.success) {
          toast.success('Borrow request submitted successfully');
          setBorrows(response.data);
          setOpenBorrowDialog(false);
          setFormData({ equipment: '', duration: '', type: '', destination: '', description: '', date: '' });
        } else {
          toast.error(response.message || 'Failed to submit borrow request');
        }
      }
    } catch (err) {
      toast.error('Network error: Failed to submit borrow request');
      console.error('Borrow submit error:', err);
    }
  };

  const handleEditBorrow = (borrow) => {
    setIsEditMode(true);
    setSelectedBorrowId(borrow.id);
    setFormData({
      equipment: borrow.equipment,
      duration: borrow.duration,
      type: borrow.type,
      destination: borrow.destination,
      description: borrow.description,
      date: borrow.date
    });
    setOpenBorrowDialog(true);
  };

  const handleCancelBorrow = async () => {
    try {
      const response = await deleteData('proccess.php', 'borrow_V2', selectedBorrowId);
      if (response.success) {
        toast.success('Borrow request cancelled successfully');
        setBorrows(borrows.filter(borrow => borrow.id !== selectedBorrowId));
        setOpenCancelDialog(false);
        setSelectedBorrowId(null);
      } else {
        toast.error(response.message || 'Failed to cancel borrow request');
      }
    } catch (err) {
      toast.error('Network error: Failed to cancel borrow request');
      console.error('Cancel error:', err);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5, minWidth: 50 },
    { field: 'equipment', headerName: 'Equipment', flex: 1, minWidth: 120 },
    { field: 'date', headerName: 'Borrow Date', flex: 1, minWidth: 120 },
    { field: 'duration', headerName: 'Duration', flex: 1, minWidth: 120 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <StatusChip colors={colors} status={params.row.status} />
      )
    }
  ];

  const availableEquipment = items.filter(item => (item.categoryType === 'Laptop' || item.categoryType === 'Charger') && item.status === 0);
  const currentBorrows = borrows.filter(borrow => borrow.status < 4);

  return (
    <Box mt={2}>
      {loading ? (
        <Loading ui={ui} label="Loading your borrowing…" />
      ) : (
        <>
          <Box width="100%" display="flex" justifyContent="end">
            <Button
              startIcon={<LaptopIcon />}
              onClick={() => {
                setIsEditMode(false);
                setFormData({ equipment: '', duration: '', type: '', destination: '', description: '', date: '' });
                setOpenBorrowDialog(true);
              }}
              sx={{ ...primaryBtn(ui), mb: 2 }}
            >
              Borrow Equipment
            </Button>
          </Box>
          <Box display="flex" justifyContent="space-between" flexWrap="wrap-reverse">
            <Box flex="1" maxWidth="1000px" minWidth="350px" m="0.3rem">
              <SectionCard
                ui={ui}
                title="Available equipment"
                hint="Laptops and chargers on the shelf right now."
                action={
                  <Chip
                    size="small"
                    label={availableEquipment.length}
                    sx={{
                      fontWeight: 700,
                      color: ui.accent,
                      bgcolor: alpha(ui.accent, 0.14),
                    }}
                  />
                }
              >
                <Box maxHeight="40vh" overflow="auto">
                  {availableEquipment.length > 0 ? (
                    <Stack gap={1}>
                      {availableEquipment.map((item) => (
                        <Box
                          key={item.id}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: ui.surfaceAlt,
                            border: `1px solid ${ui.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.5,
                              display: 'grid',
                              placeItems: 'center',
                              color: colors.green[500],
                              bgcolor: alpha(colors.green[500], 0.14),
                            }}
                          >
                            <LaptopIcon sx={{ fontSize: 18 }} />
                          </Box>

                          <Box flex={1} minWidth={0}>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color={ui.textPrimary}
                              noWrap
                            >
                              {item.name}
                            </Typography>
                            <Typography variant="caption" color={ui.textSecondary} noWrap>
                              {item.Type} · ID {item.id}
                              {item.dateCreated ? ` · added ${item.dateCreated}` : ''}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label="Available"
                            sx={{
                              fontWeight: 700,
                              color: colors.green[500],
                              bgcolor: alpha(colors.green[500], 0.16),
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <EmptyState
                      ui={ui}
                      icon={<InboxOutlinedIcon />}
                      title="Nothing on the shelf"
                      hint="Everything is out on loan or out of service."
                    />
                  )}
                </Box>
              </SectionCard>
            </Box>

            <Box flex="1" maxWidth="700px" minWidth="350px" m="0.3rem">
              <SectionCard
                ui={ui}
                title="Current borrowing"
                hint="Pending requests can still be changed."
              >
                <Box maxHeight="40vh" overflow="auto">
                  {currentBorrows.length > 0 ? (
                    currentBorrows.map((borrow) => (
                      <RecordCard
                        ui={ui}
                        colors={colors}
                        key={borrow.id}
                        status={borrow.status}
                        title={borrow.equipment}
                      >
                        <DetailRow ui={ui} label="Borrowed on" value={borrow.date} />
                        <DetailRow
                          ui={ui}
                          label="Duration"
                          value={borrow.duration}
                          color={ui.accent}
                        />
                        <DetailRow ui={ui} label="Destination" value={borrow.destination} />
                        <DetailRow
                          ui={ui}
                          label="Decided on"
                          value={borrow.supervisor_appr_date || borrow.rejected_date}
                        />
                        <DetailRow ui={ui} label="Notes" value={borrow.description} />

                        {/* Only while nobody has acted on it. */}
                        {Number(borrow.status) === 0 && (
                          <Box display="flex" gap={1} mt={1.5}>
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditBorrow(borrow)}
                              sx={outlineBtn(ui)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              startIcon={<DeleteIcon />}
                              onClick={() => {
                                setSelectedBorrowId(borrow.id);
                                setOpenCancelDialog(true);
                              }}
                              sx={outlineBtn(ui, colors.red[500])}
                            >
                              Cancel
                            </Button>
                          </Box>
                        )}
                      </RecordCard>
                    ))
                  ) : (
                    <EmptyState
                      ui={ui}
                      icon={<LaptopIcon />}
                      title="Nothing borrowed"
                      hint="Use Borrow Equipment to raise a request."
                    />
                  )}
                </Box>
              </SectionCard>
            </Box>
          </Box>

          <SectionCard
            ui={ui}
            title="Borrowing history"
            hint="Everything you have ever borrowed."
            sx={{ mt: 2.5 }}
          >
            <Box height="400px">
              <DataGrid
                rows={borrows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
                components={{ Toolbar: GridToolbarQuickFilter }}
                sx={{
                  border: 'none',
                  color: ui.textPrimary,
                  '& .MuiDataGrid-cell': {
                    borderBottom: `1px solid ${alpha(ui.border, 0.7)}`,
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    background: ui.gradient,
                    color: '#fff',
                    borderBottom: 'none',
                    fontWeight: 700,
                  },
                  '& .MuiDataGrid-columnSeparator': { display: 'none' },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: `1px solid ${ui.border}`,
                    color: ui.textSecondary,
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: alpha(ui.accent, 0.07),
                  },
                  '& .MuiDataGrid-virtualScroller': { backgroundColor: ui.surface },
                  '& .MuiTablePagination-root': { color: ui.textSecondary },
                }}
              />
            </Box>
          </SectionCard>

          <Dialog
            open={openBorrowDialog}
            onClose={(e, reason) => {
              // Not on a stray click outside - people lose what they typed.
              if (reason === 'backdropClick') return;
              setOpenBorrowDialog(false);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: dialogPaperSx(ui) }}
          >
            <DialogTitle sx={{ color: ui.textPrimary, fontWeight: 700 }}>
              {isEditMode ? 'Edit borrow request' : 'Borrow equipment'}
            </DialogTitle>
            <Divider sx={{ borderColor: ui.border }} />
            <DialogContent sx={{ pt: 3 }}>
              <Box component="form" onSubmit={handleBorrowSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <TextField
                  select
                  label="Equipment (ID)"
                  name="equipment"
                  value={formData.equipment}
                  onChange={handleFormChange}
                  sx={fieldSx(ui)}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value=""></option>
                  {items.map((equipment) => (
                    <option key={equipment.id} value={equipment.id}>
                      {equipment.name} (SKU: {equipment.sku})
                    </option>
                  ))}
                </TextField>
                <TextField
                  label="Duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleFormChange}
                  sx={fieldSx(ui)}
                  required
                />
                <TextField
                  select
                  label="Borrow Type"
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  sx={fieldSx(ui)}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value=""></option>
                  <option value="WorkHours">Working Hours</option>
                  <option value="Loaning">Loaning</option>
                </TextField>
                <TextField
                  select
                  label="Destination"
                  name="destination"
                  value={formData.destination}
                  onChange={handleFormChange}
                  sx={fieldSx(ui)}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value=""></option>
                  <option value="Work">Work</option>
                  <option value="Outofwork">Out Of Office</option>
                </TextField>
                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  sx={fieldSx(ui)}
                  multiline
                  rows={2}
                />
              </Box>
            </DialogContent>
            <Divider sx={{ borderColor: ui.border }} />
            <DialogActions sx={{ px: 3, py: 2.5 }}>
              <Button
                onClick={() => setOpenBorrowDialog(false)}
                sx={{ textTransform: 'none', color: ui.textSecondary }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={handleBorrowSubmit}
                endIcon={<SendIcon />}
                sx={primaryBtn(ui)}
              >
                {isEditMode ? 'Update' : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openCancelDialog}
            onClose={() => setOpenCancelDialog(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: dialogPaperSx(ui) }}
          >
            <DialogTitle sx={{ color: ui.textPrimary, fontWeight: 700 }}>
              Cancel this request?
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color={ui.textSecondary}>
                The request will be withdrawn. You can borrow again afterwards.
              </Typography>
            </DialogContent>
            <Divider sx={{ borderColor: ui.border }} />
            <DialogActions sx={{ px: 3, py: 2.5 }}>
              <Button
                onClick={() => setOpenCancelDialog(false)}
                sx={{ textTransform: 'none', color: ui.textSecondary }}
              >
                Keep it
              </Button>
              <Button
                onClick={handleCancelBorrow}
                endIcon={<DeleteIcon />}
                sx={outlineBtn(ui, colors.red[500])}
              >
                Yes, cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default Borrow;